import React, {
  FC,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Grid } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import { BakeasyChip } from '../../components/common/BakeasyChip';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import { t } from 'i18next';
import { formatDateTime } from '../../utils/DateUtils';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { useNavigate } from 'react-router-dom';
import {
  downloadStores,
  fetchStores,
  storesState,
} from '../../redux_store/slices/storesSlice';
import { authActions } from '../../redux_store/slices/authSlice';
import { UNAUTHORIZED } from '../../constants/Constants';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import * as BakeasyButton from '../../components/buttons/BakeasyButtons';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { storeTableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Stores: FC = (): ReactElement => {
  const navigate = useNavigate();
  const tableRef = useRef<any>();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  let myContext = useContext(AppContext);

  const [search, setSearch] = useState<string>('');
  const [storesList, setStoresList] = useState<any[]>([]);
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } =
    useAppSelector(storesState);

  useEffect(() => {
    if ('search' in filter) {
      let value: any = filter?.search;
      setSearch(value);
    }
  }, [filter]);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  function openContactStoreConfirmDialog(store: any) {
    myContext.setConfirmDialog({
      type: 'contact',
      open: true,
      title: t('CONTACT_STORE'),
      data: store?.contact1,
    });
  }

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((store: any) => {
        return {
          id: store._id,
          storeName: store?.storeName,
          storeId: <BakeasyChip label={`${store._id.toUpperCase()}`} />,
          onStoreNameClick: () => {
            navigate('/storeDetails', {
              state: { isView: true, _id: store._id },
            });
          },
          supplierName: store.supplierName,
          onSupplierNameClick: () => {
            navigate('/addEditSupplier', {
              state: { isView: true, isEdit: false, _id: store?.supplierId },
            });
          },
          contact: store?.contact1?.firstName
            ? store?.contact1?.firstName
            : '' + ' ' + store?.contact1?.lastName
              ? store?.contact1?.lastName
              : '',
          onContactClick: () => {
            openContactStoreConfirmDialog(store);
          },
          createdDate: store?.createdDate
            ? formatDateTime(store?.createdDate)
            : '',
          lastOrderDate: store?.lastOrderDate
            ? formatDateTime(store?.lastOrderDate)
            : '',
          totalOrders: store?.totalOrders ?? '',
          totalOrderValue:
            store?.totalOrderValue || store?.totalOrderValue === 0
              ? parseFloat(store?.totalOrderValue).toFixed(2)
              : parseFloat('0')?.toFixed(2),
          status: <BakeasyChip label={store?.status.toUpperCase()} />,
        };
      });

      setStoresList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (
    newPage: number,
    search?: string,
    filter?: {} | undefined
  ) => {
    let payload: any = {
      limit: rowsPerPage,
      page: newPage + 1,
      sortBy: 'createdDate',
      sortDirection: -1,
    };

    if (filter != undefined && filter !== '') {
      payload = { ...payload, ...filter };
    }

    payload = { ...payload, page: newPage + 1 };

    if (search !== undefined && search !== '') {
      payload = { ...payload, search };
    }

    if (search === undefined || search === '') {
      delete payload['search'];
    }

    await dispatch(fetchStores(payload));
    // await fetchData(fetchStores, payload);
  };

  function openFilterStoresConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'store-filter',
      open: true,
      title: t('STORE_FILTERS'),
      data: filter,
      onNoClick: (event: any) => {},
      onYesClick: (event: any, param: string | undefined) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      },
    });
  }

  const handleDelete = async (key: any) => {
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function storesUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('STORES')} />

          <Grid
            container
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (
                    event?.target?.value?.length === 0 ||
                    event?.target?.value?.length > 2
                  ) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0
                        ? undefined
                        : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(
                  event: React.KeyboardEvent<HTMLInputElement>
                ) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={9} lg={6} xl={6}>
              <div className="tableOptions">
                <BakeasyButton.Print
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadStores({ ...filter, downloadType: 'pdf' })
                    );
                  }}
                />
                <BakeasyButton.Export
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    dispatch(
                      downloadStores({ ...filter, downloadType: 'csv' })
                    );
                  }}
                />
                <BakeasyButton.Filter
                  onClick={function (
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): void {
                    openFilterStoresConfirmDialog();
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />

          <Grid>
            {storesList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={storeTableHeaders}
                tableData={storesList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return storesUI();
};
